import React, { useState, useEffect } from 'react';
import { api } from '../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import BankStatementUpload from '../components/BankStatementUpload';
import { DesktopOnlyFeature, useIsMobile } from '../lib/responsive';
import {
  FileText,
  Download,
  RefreshCw,
  Calendar,
  Upload,
  Bell,
  Wifi,
  WifiOff,
  Cloud,
  CheckCircle2,
  Lock,
  FileBarChart,
  Monitor
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  isNotificationSupported, 
  requestNotificationPermission, 
  getNotificationPermission,
  getReceivableReminders,
  formatReminder
} from '../lib/notifications';
import { 
  isOnline, 
  syncToServer, 
  downloadFromServer,
  getPendingOfflineCount,
  initOfflineSync
} from '../lib/offlineSync';

export default function Reports() {
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bankUploadOpen, setBankUploadOpen] = useState(false);
  const [ventures, setVentures] = useState([]);
  const [locations, setLocations] = useState([]);
  const [reminders, setReminders] = useState([]);
  const isMobile = useIsMobile();
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [online, setOnline] = useState(isOnline());
  const [pendingSync, setPendingSync] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [pnlData, setPnlData] = useState(null);
  const [pnlMonth, setPnlMonth] = useState(new Date().getMonth() + 1);
  const [pnlYear, setPnlYear] = useState(new Date().getFullYear());
  const [pnlLoading, setPnlLoading] = useState(false);
  const [backupDialogOpen, setBackupDialogOpen] = useState(false);
  const [backupPassword, setBackupPassword] = useState('');
  const [backupLoading, setBackupLoading] = useState(false);
  const [bankMonthlySummary, setBankMonthlySummary] = useState([]);
  const [bankSummaryLoading, setBankSummaryLoading] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [fixLoading, setFixLoading] = useState(false);

  useEffect(() => {
    fetchData();
    fetchBankMonthlySummary();
    checkNotificationStatus();
    checkOfflineData();
    
    // Setup online/offline listeners
    const cleanup = initOfflineSync((status) => {
      setOnline(status.online);
      if (status.synced) {
        toast.success(`Synced ${status.synced} offline items`);
        checkOfflineData();
      }
    });
    
    return cleanup;
  }, []);

  const fetchData = async () => {
    try {
      const [venturesRes, locationsRes] = await Promise.all([
        api.get('/ventures'),
        api.get('/locations'),
      ]);
      setVentures(venturesRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchBankMonthlySummary = async () => {
    setBankSummaryLoading(true);
    try {
      const res = await api.get('/bank-statement/monthly-summary');
      setBankMonthlySummary(res.data.monthly_summaries || []);
    } catch (error) {
      console.error('Error fetching bank monthly summary:', error);
    } finally {
      setBankSummaryLoading(false);
    }
  };

  const runAudit = async () => {
    setAuditLoading(true);
    try {
      const res = await api.get('/reports/audit');
      setAuditData(res.data);
    } catch (err) {
      toast.error('Audit failed');
    } finally {
      setAuditLoading(false);
    }
  };

  const fixMisrouted = async () => {
    if (!window.confirm('This will move misrouted transactions to their correct collections. Continue?')) return;
    setFixLoading(true);
    try {
      const res = await api.post('/reports/fix-misrouted');
      toast.success(res.data.message);
      runAudit();
      fetchPnl();
    } catch (err) {
      toast.error('Fix failed');
    } finally {
      setFixLoading(false);
    }
  };


  const formatMonth = (monthStr) => {
    if (!monthStr || monthStr === 'unknown') return 'Unknown';
    const [year, month] = monthStr.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getCategoryLabel = (cat) => {
    const labels = {
      ingredients: 'Ingredients', packaging: 'Packaging', kitchen_supplies: 'Kitchen',
      salaries: 'Salaries', freelancers: 'Freelancers', rent: 'Rent', utilities: 'Utilities',
      marketing: 'Marketing', influencers: 'Influencers', equipment: 'Equipment',
      repairs: 'Repairs', software: 'Software', transportation: 'Transport',
      owner_withdrawal: 'Owner', credit_card_settlement: 'CC Settlement',
      investment: 'Investment', loan_emi: 'Loan EMI', revenue: 'Revenue', other: 'Other'
    };
    return labels[cat] || cat;
  };

  const checkNotificationStatus = () => {
    setNotificationPermission(getNotificationPermission());
  };

  const checkOfflineData = async () => {
    const counts = await getPendingOfflineCount();
    setPendingSync(counts.total);
  };

  const generateWeeklyReport = async () => {
    setLoading(true);
    try {
      const res = await api.get('/weekly-report');
      setWeeklyReport(res.data);
      toast.success('Report generated successfully');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEnableNotifications = async () => {
    const result = await requestNotificationPermission();
    setNotificationPermission(result.permission);
    if (result.granted) {
      toast.success('Notifications enabled! You\'ll receive reminders for pending receivables.');
    } else {
      toast.error('Notification permission denied');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await syncToServer();
      if (result.success) {
        toast.success(result.message);
        checkOfflineData();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleDownload = async () => {
    setSyncing(true);
    try {
      const result = await downloadFromServer();
      if (result.success) {
        toast.success('Data downloaded for offline use');
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Download failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleExport = async (type) => {
    try {
      const response = await api.get(`/export/${type}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `roch_${type}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${type.replace('-', ' ')} exported successfully`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    }
  };

  const generatePnL = async (fmt = 'json') => {
    setPnlLoading(true);
    try {
      if (fmt === 'pdf') {
        const response = await api.get(`/reports/pnl?month=${pnlMonth}&year=${pnlYear}&format=pdf`, {
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `ROCH_PnL_${pnlYear}_${pnlMonth}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('P&L PDF downloaded');
      } else {
        const res = await api.get(`/reports/pnl?month=${pnlMonth}&year=${pnlYear}`);
        setPnlData(res.data);
        toast.success('P&L statement generated');
      }
    } catch (error) {
      toast.error('Failed to generate P&L report');
    } finally {
      setPnlLoading(false);
    }
  };

  const handleBackup = async () => {
    if (!backupPassword || backupPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setBackupLoading(true);
    try {
      const response = await api.post('/backup/create', { password: backupPassword }, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ROCH_backup_${new Date().toISOString().split('T')[0]}.enc`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Encrypted backup downloaded');
      setBackupDialogOpen(false);
      setBackupPassword('');
    } catch (error) {
      toast.error('Failed to create backup');
    } finally {
      setBackupLoading(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-900">Reports & Tools</h1>
          <p className="text-zinc-500 mt-1">AI-powered insights, imports, and notifications</p>
        </div>
        <div className="flex items-center gap-2">
          {online ? (
            <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
              <Wifi className="w-3 h-3" /> Online
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
              <WifiOff className="w-3 h-3" /> Offline
            </span>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {/* Bank Statement Import */}
        {/* Bank Statement Upload - Desktop Only */}
        {isMobile ? (
          <Card className="roch-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-zinc-100 rounded-xl flex items-center justify-center">
                  <Monitor className="w-6 h-6 text-zinc-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-zinc-900">Import Bank Statement</h3>
                  <p className="text-sm text-zinc-500">Use desktop for uploads</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="roch-card cursor-pointer hover:border-amber-200 transition-colors" onClick={() => setBankUploadOpen(true)}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-500 rounded-xl flex items-center justify-center">
                  <Upload className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-zinc-900">Import Bank Statement</h3>
                  <p className="text-sm text-zinc-500">AI-powered categorization</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notifications */}
        <Card className={`roch-card cursor-pointer transition-colors ${notificationPermission === 'granted' ? 'border-green-200' : 'hover:border-amber-200'}`}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${notificationPermission === 'granted' ? 'bg-green-100' : 'bg-zinc-100'}`}>
                <Bell className={`w-6 h-6 ${notificationPermission === 'granted' ? 'text-green-600' : 'text-zinc-600'}`} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-zinc-900">Push Notifications</h3>
                <p className="text-sm text-zinc-500">
                  {notificationPermission === 'granted' ? 'Enabled' : 'Get payment reminders'}
                </p>
              </div>
              {notificationPermission !== 'granted' && isNotificationSupported() && (
                <Button size="sm" variant="outline" onClick={handleEnableNotifications}>
                  Enable
                </Button>
              )}
              {notificationPermission === 'granted' && (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Offline Sync */}
        <Card className="roch-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${pendingSync > 0 ? 'bg-amber-100' : 'bg-zinc-100'}`}>
                <Cloud className={`w-6 h-6 ${pendingSync > 0 ? 'text-amber-600' : 'text-zinc-600'}`} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-zinc-900">Offline Data</h3>
                <p className="text-sm text-zinc-500">
                  {pendingSync > 0 ? `${pendingSync} items pending` : 'All synced'}
                </p>
              </div>
              <div className="flex gap-2">
                {pendingSync > 0 && (
                  <Button size="sm" variant="outline" onClick={handleSync} disabled={syncing || !online}>
                    {syncing ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Sync'}
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={handleDownload} disabled={syncing || !online}>
                  <Download className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Weekly Report Section */}
      <Card className="roch-card mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-semibold flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-500" />
              Weekly Financial Summary
            </CardTitle>
            <Button
              onClick={generateWeeklyReport}
              disabled={loading}
              className="rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
              data-testid="generate-report-btn"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Generate Report
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!weeklyReport ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FileText className="w-7 h-7 text-amber-500" />
              </div>
              <h3 className="font-medium text-zinc-900 mb-2">No report generated yet</h3>
              <p className="text-sm text-zinc-500 mb-6 max-w-md mx-auto">
                Generate an AI-powered weekly financial summary with insights, trends, and action items.
              </p>
              <Button onClick={generateWeeklyReport} disabled={loading} className="bg-gradient-to-r from-amber-500 to-amber-600">
                Generate Weekly Report
              </Button>
            </div>
          ) : (
            <div>
              {/* Report Metadata */}
              <div className="flex items-center gap-4 mb-6 pb-4 border-b border-zinc-100">
                <div className="text-sm text-zinc-500">
                  Period: {weeklyReport.period?.start} to {weeklyReport.period?.end}
                </div>
              </div>

              {/* Quick Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-gradient-to-r from-amber-50 to-white rounded-xl border border-amber-100">
                <div>
                  <div className="text-xs uppercase tracking-wider text-amber-600 mb-1">Revenue</div>
                  <div className="text-lg font-semibold" data-testid="weekly-revenue">
                    ₹{(weeklyReport.summary?.total_revenue || 0).toLocaleString('en-IN')}
                  </div>
                  <div className={`text-xs ${weeklyReport.summary?.revenue_change_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {weeklyReport.summary?.revenue_change_pct >= 0 ? '+' : ''}{weeklyReport.summary?.revenue_change_pct}% vs last week
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Expenses</div>
                  <div className="text-lg font-semibold" data-testid="weekly-expenses">
                    ₹{(weeklyReport.summary?.total_expenses || 0).toLocaleString('en-IN')}
                  </div>
                  <div className={`text-xs ${weeklyReport.summary?.expense_change_pct <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {weeklyReport.summary?.expense_change_pct >= 0 ? '+' : ''}{weeklyReport.summary?.expense_change_pct}%
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Net Profit</div>
                  <div className={`text-lg font-semibold ${(weeklyReport.summary?.net_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="weekly-profit">
                    ₹{(weeklyReport.summary?.net_profit || 0).toLocaleString('en-IN')}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Margin</div>
                  <div className="text-lg font-semibold" data-testid="weekly-margin">
                    {weeklyReport.summary?.profit_margin || 0}%
                  </div>
                </div>
              </div>

              {/* Revenue by Venture */}
              {weeklyReport.revenue_by_venture && Object.keys(weeklyReport.revenue_by_venture).length > 0 && (
                <div className="mb-6">
                  <h3 className="font-medium text-sm text-zinc-900 mb-3">Revenue by Venture</h3>
                  <div className="space-y-2">
                    {Object.entries(weeklyReport.revenue_by_venture).map(([name, amount]) => (
                      <div key={name} className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                        <span className="text-sm font-medium">{name}</span>
                        <span className="text-sm font-semibold">₹{amount.toLocaleString('en-IN')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Expenses */}
              {weeklyReport.top_expenses && weeklyReport.top_expenses.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-medium text-sm text-zinc-900 mb-3">Top Expenses</h3>
                  <div className="space-y-2">
                    {weeklyReport.top_expenses.map((exp, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                        <div>
                          <span className="text-sm font-medium">{exp.vendor}</span>
                          <span className="ml-2 text-xs text-zinc-500">{exp.category}</span>
                        </div>
                        <span className="text-sm font-semibold text-red-600">₹{exp.amount.toLocaleString('en-IN')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Receivables & Transactions Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-zinc-50 rounded-xl border border-zinc-100">
                  <div className="text-xs uppercase text-zinc-500 mb-1">Transactions</div>
                  <div className="text-lg font-semibold">{(weeklyReport.transaction_count?.revenues || 0) + (weeklyReport.transaction_count?.expenses || 0)}</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {weeklyReport.transaction_count?.revenues || 0} rev, {weeklyReport.transaction_count?.expenses || 0} exp
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Export Options */}
      <Card className="roch-card">
        <CardHeader>
          <CardTitle className="text-base font-medium">Export Data</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 border border-zinc-200 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-zinc-100 rounded-lg flex items-center justify-center">
                  <Download className="w-5 h-5 text-zinc-600" />
                </div>
                <div>
                  <h3 className="font-medium">Transactions</h3>
                  <p className="text-xs text-zinc-500">Revenue & expenses</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full mt-2"
                data-testid="export-transactions-btn"
                onClick={() => handleExport('transactions')}
              >
                <Download className="w-3 h-3 mr-1" /> CSV
              </Button>
            </div>

            <div className="p-4 border border-zinc-200 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-zinc-100 rounded-lg flex items-center justify-center">
                  <Download className="w-5 h-5 text-zinc-600" />
                </div>
                <div>
                  <h3 className="font-medium">Tax Report</h3>
                  <p className="text-xs text-zinc-500">For ITR filing</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full mt-2"
                data-testid="export-tax-btn"
                onClick={() => handleExport('tax-report')}
              >
                <Download className="w-3 h-3 mr-1" /> CSV
              </Button>
            </div>

            <div className="p-4 border border-zinc-200 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-zinc-100 rounded-lg flex items-center justify-center">
                  <Download className="w-5 h-5 text-zinc-600" />
                </div>
                <div>
                  <h3 className="font-medium">Vendor Report</h3>
                  <p className="text-xs text-zinc-500">Vendor-wise spending</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full mt-2"
                data-testid="export-vendor-btn"
                onClick={() => handleExport('vendor-report')}
              >
                <Download className="w-3 h-3 mr-1" /> CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bank Statement Upload Dialog */}
      <BankStatementUpload
        open={bankUploadOpen}
        onOpenChange={setBankUploadOpen}
        ventures={ventures}
        locations={locations}
        onSuccess={() => { fetchData(); fetchBankMonthlySummary(); }}
      />

      {/* Monthly Summary - Overall Totals */}
      <Card className="roch-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Monthly Summary
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchBankMonthlySummary} disabled={bankSummaryLoading}>
              <RefreshCw className={`w-4 h-4 ${bankSummaryLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {bankMonthlySummary.length === 0 ? (
            <div className="text-center py-8 text-sm text-zinc-500">
              No transactions found. Upload a statement to see monthly summaries.
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {bankMonthlySummary.map((month) => (
                <div key={month.month} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-zinc-900">{formatMonth(month.month)}</h4>
                      <p className="text-xs text-zinc-500">{month.transaction_count} transactions</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-bold ${month.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {month.net >= 0 ? '+' : ''}{formatCurrency(month.net)}
                      </p>
                      <p className="text-xs text-zinc-500">Net</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div className="bg-emerald-50 rounded-lg p-3">
                      <p className="text-xs text-emerald-600 font-medium">Revenue</p>
                      <p className="text-lg font-semibold text-emerald-700">{formatCurrency(month.total_revenue)}</p>
                      <p className="text-xs text-emerald-500">{month.revenue_count} credits</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3">
                      <p className="text-xs text-red-600 font-medium">Expenses</p>
                      <p className="text-lg font-semibold text-red-700">{formatCurrency(month.total_expenses)}</p>
                      <p className="text-xs text-red-500">{month.expense_count} debits</p>
                    </div>
                  </div>
                  {Object.keys(month.by_category || {}).length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(month.by_category).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([cat, amt]) => (
                        <span key={cat} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-zinc-100 text-xs">
                          <span className="text-zinc-600">{getCategoryLabel(cat)}</span>
                          <span className="font-medium">{formatCurrency(amt)}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Data Quality Audit */}
      <Card className="roch-card" data-testid="data-audit-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Data Quality Audit
            </CardTitle>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={runAudit} disabled={auditLoading} data-testid="run-audit-btn">
                {auditLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                Run Audit
              </Button>
              {auditData?.issues?.misrouted_revenues_count > 0 || auditData?.issues?.misrouted_expenses_count > 0 ? (
                <Button size="sm" variant="destructive" onClick={fixMisrouted} disabled={fixLoading} data-testid="fix-misrouted-btn">
                  {fixLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                  Fix Misrouted ({(auditData?.issues?.misrouted_revenues_count || 0) + (auditData?.issues?.misrouted_expenses_count || 0)})
                </Button>
              ) : null}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!auditData ? (
            <p className="text-sm text-zinc-500">Click "Run Audit" to check your data for issues like misrouted transactions and duplicates.</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="p-3 rounded-lg bg-zinc-50 border">
                  <div className="text-xs text-zinc-500">Total Expenses</div>
                  <div className="text-lg font-semibold">{auditData.summary.expense_count}</div>
                  <div className="text-sm text-red-600">₹{auditData.summary.total_expenses?.toLocaleString('en-IN')}</div>
                </div>
                <div className="p-3 rounded-lg bg-zinc-50 border">
                  <div className="text-xs text-zinc-500">Total Revenues</div>
                  <div className="text-lg font-semibold">{auditData.summary.revenue_count}</div>
                  <div className="text-sm text-green-600">₹{auditData.summary.total_revenues?.toLocaleString('en-IN')}</div>
                </div>
                <div className={`p-3 rounded-lg border ${auditData.summary.net_profit >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="text-xs text-zinc-500">Net P&L</div>
                  <div className={`text-lg font-semibold ${auditData.summary.net_profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>₹{auditData.summary.net_profit?.toLocaleString('en-IN')}</div>
                </div>
                <div className={`p-3 rounded-lg border ${auditData.issues.misrouted_revenues_count > 0 ? 'bg-amber-50 border-amber-300' : 'bg-zinc-50'}`}>
                  <div className="text-xs text-zinc-500">Misrouted</div>
                  <div className="text-lg font-semibold text-amber-700">{auditData.issues.misrouted_revenues_count + auditData.issues.misrouted_expenses_count}</div>
                  <div className="text-[11px] text-zinc-400">expenses in revenues or vice versa</div>
                </div>
                <div className={`p-3 rounded-lg border ${auditData.issues.potential_duplicate_expenses > 0 ? 'bg-amber-50 border-amber-300' : 'bg-zinc-50'}`}>
                  <div className="text-xs text-zinc-500">Potential Duplicates</div>
                  <div className="text-lg font-semibold text-amber-700">{auditData.issues.potential_duplicate_expenses + auditData.issues.potential_duplicate_revenues}</div>
                  <div className="text-[11px] text-zinc-400">same date + amount</div>
                </div>
              </div>
              {auditData.issues.misrouted_revenues_count > 0 && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <p className="text-sm font-medium text-amber-800 mb-2">Expenses found in Revenues collection ({auditData.issues.misrouted_revenues_count} items, ₹{auditData.issues.misrouted_revenues_amount?.toLocaleString('en-IN')})</p>
                  <div className="text-xs text-amber-600 space-y-0.5 max-h-32 overflow-auto">
                    {auditData.issues.misrouted_revenues.map((r, i) => (
                      <div key={i}>{r.date} — {r.source || r.category} — ₹{r.amount?.toLocaleString('en-IN')} (cat: {r.category})</div>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-xs text-zinc-400">Period: {auditData.period.start} to {auditData.period.end}</div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Monthly P&L Statement */}
      <Card className="roch-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <FileBarChart className="w-4 h-4" />
              Monthly P&L Statement
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3 mb-6">
            <div>
              <Label className="text-xs">Month</Label>
              <select
                value={pnlMonth}
                onChange={(e) => setPnlMonth(parseInt(e.target.value))}
                className="block mt-1 px-3 py-2 border rounded-lg text-sm"
                data-testid="pnl-month-select"
              >
                {[...Array(12)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(2000, i).toLocaleString('default', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Year</Label>
              <select
                value={pnlYear}
                onChange={(e) => setPnlYear(parseInt(e.target.value))}
                className="block mt-1 px-3 py-2 border rounded-lg text-sm"
              >
                {[2024, 2025, 2026].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <Button onClick={() => generatePnL('json')} disabled={pnlLoading} data-testid="pnl-generate-btn">
              {pnlLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileBarChart className="w-4 h-4 mr-2" />}
              Generate
            </Button>
            <Button variant="outline" onClick={() => generatePnL('pdf')} disabled={pnlLoading} data-testid="pnl-pdf-btn">
              <Download className="w-4 h-4 mr-2" />Download PDF
            </Button>
          </div>

          {pnlData && (
            <div className="space-y-4" data-testid="pnl-results">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gradient-to-r from-zinc-50 to-white rounded-xl border">
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Revenue</div>
                  <div className="text-lg font-semibold">₹{(pnlData.revenue?.total || 0).toLocaleString('en-IN')}</div>
                  <div className={`text-xs ${pnlData.comparison?.revenue_change_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {pnlData.comparison?.revenue_change_pct >= 0 ? '+' : ''}{pnlData.comparison?.revenue_change_pct}% MoM
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Operating Expenses</div>
                  <div className="text-lg font-semibold">₹{(pnlData.expenses?.total || 0).toLocaleString('en-IN')}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Operating Profit</div>
                  <div className={`text-lg font-semibold ${(pnlData.operating_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ₹{(pnlData.operating_profit || 0).toLocaleString('en-IN')}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Investments</div>
                  <div className="text-lg font-semibold text-blue-600">₹{(pnlData.investments?.total || 0).toLocaleString('en-IN')}</div>
                  <div className="text-xs text-zinc-500">Profit allocation</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Remaining Profit</div>
                  <div className={`text-lg font-semibold ${(pnlData.remaining_profit || 0) >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}>
                    ₹{(pnlData.remaining_profit || 0).toLocaleString('en-IN')}
                  </div>
                  <div className="text-xs text-zinc-500">Margin: {pnlData.margin_pct}%</div>
                </div>
              </div>

              {pnlData.revenue?.by_venture && Object.keys(pnlData.revenue.by_venture).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Revenue by Venture</h4>
                  <div className="space-y-1.5">
                    {Object.entries(pnlData.revenue.by_venture).sort((a, b) => b[1] - a[1]).map(([name, amt]) => (
                      <div key={name} className="flex justify-between items-center p-2.5 bg-zinc-50 rounded-lg text-sm">
                        <span>{name}</span>
                        <span className="font-medium">₹{amt.toLocaleString('en-IN')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {pnlData.expenses?.by_category && Object.keys(pnlData.expenses.by_category).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Operating Expenses by Category</h4>
                  <div className="space-y-1.5">
                    {Object.entries(pnlData.expenses.by_category).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
                      <div key={cat} className="flex justify-between items-center p-2.5 bg-zinc-50 rounded-lg text-sm">
                        <span>{cat.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                        <span className="font-medium text-red-600">₹{amt.toLocaleString('en-IN')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {pnlData.investments?.total > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Investment Report (Profit Allocation)</h4>
                  <div className="space-y-1.5">
                    {pnlData.investments.by_type && Object.entries(pnlData.investments.by_type).sort((a, b) => b[1] - a[1]).map(([type, amt]) => (
                      <div key={type} className="flex justify-between items-center p-2.5 bg-blue-50 rounded-lg text-sm">
                        <span>{type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                        <span className="font-medium text-blue-600">₹{amt.toLocaleString('en-IN')}</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center p-2.5 bg-blue-100 rounded-lg text-sm font-medium">
                      <span>Total Investments</span>
                      <span className="text-blue-700">₹{pnlData.investments.total.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
              )}

              {pnlData.by_bank_account && Object.keys(pnlData.by_bank_account).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Bank Account Breakdown</h4>
                  <div className="space-y-1.5">
                    {Object.entries(pnlData.by_bank_account).sort((a, b) => Math.abs(b[1].net) - Math.abs(a[1].net)).map(([name, data]) => (
                      <div key={name} className="flex justify-between items-center p-2.5 bg-zinc-50 rounded-lg text-sm">
                        <div>
                          <span className="font-medium">{name}</span>
                          <span className="text-zinc-400 text-xs ml-2">{data.credit_count + data.debit_count} txns</span>
                        </div>
                        <div className="flex items-center gap-4 text-xs">
                          <span className="text-emerald-600">+₹{data.credits.toLocaleString('en-IN')}</span>
                          <span className="text-red-600">-₹{data.debits.toLocaleString('en-IN')}</span>
                          <span className={`font-semibold ${data.net >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                            {data.net >= 0 ? '+' : ''}₹{data.net.toLocaleString('en-IN')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Encrypted Backup */}
      <Card className="roch-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Encrypted Backup
            </CardTitle>
            <Button onClick={() => setBackupDialogOpen(true)} data-testid="create-backup-btn">
              <Lock className="w-4 h-4 mr-2" />Create Backup
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-zinc-500">
            Download an AES-256 encrypted backup of all your financial data. You'll need your password to restore it.
          </p>
        </CardContent>
      </Card>

      {/* Backup Password Dialog */}
      <Dialog open={backupDialogOpen} onOpenChange={setBackupDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Create Encrypted Backup
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-zinc-500">
              Enter a password to encrypt your backup. You'll need this password to restore the data.
            </p>
            <div>
              <Label>Encryption Password</Label>
              <Input
                type="password"
                value={backupPassword}
                onChange={(e) => setBackupPassword(e.target.value)}
                placeholder="Min 6 characters"
                className="mt-1.5"
                data-testid="backup-password-input"
              />
            </div>
            <Button onClick={handleBackup} disabled={backupLoading || backupPassword.length < 6} className="w-full" data-testid="backup-download-btn">
              {backupLoading ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Creating Backup...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" />Download Encrypted Backup</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
